import { BALANCE } from '../config/balance';

export interface LoopCallbacks {
  /** Fixed-step simulation update. */
  update: (dt: number) => void;
  /** Variable-rate render update. `alpha` interpolates between simulation states. */
  render: (frameDelta: number, alpha: number) => void;
}

/**
 * Fixed-step simulation with variable-rate rendering.
 * The simulation is authoritative for all gameplay behaviour, which keeps the
 * movement deterministic and testable.
 */
export class GameLoop {
  private accumulator = 0;
  private lastTime = 0;
  private running = false;
  private frameHandle: number | null = null;
  private readonly onVisibilityChange = (): void => {
    // Reset the clock so a backgrounded tab does not produce a huge catch-up.
    if (typeof document !== 'undefined' && document.hidden) this.lastTime = 0;
  };

  constructor(
    private readonly callbacks: LoopCallbacks,
    private readonly requestFrame: (cb: (time: number) => void) => number = defaultRequestFrame,
    private readonly cancelFrame: (handle: number) => void = defaultCancelFrame,
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = 0;
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
    this.frameHandle = this.requestFrame(this.tick);
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    if (this.frameHandle !== null) this.cancelFrame(this.frameHandle);
    this.frameHandle = null;
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  private readonly tick = (time: number): void => {
    if (!this.running) return;
    this.frameHandle = this.requestFrame(this.tick);

    if (this.lastTime === 0) this.lastTime = time;
    const frameDelta = Math.min((time - this.lastTime) / 1000, BALANCE.simulation.maxFrameDelta);
    this.lastTime = time;

    this.accumulator += frameDelta;
    let steps = 0;
    while (
      this.accumulator >= BALANCE.simulation.step &&
      steps < BALANCE.simulation.maxStepsPerFrame
    ) {
      this.callbacks.update(BALANCE.simulation.step);
      this.accumulator -= BALANCE.simulation.step;
      steps += 1;
    }
    if (steps === BALANCE.simulation.maxStepsPerFrame) {
      // Give up on the backlog rather than spiralling.
      this.accumulator = 0;
    }

    const alpha = this.accumulator / BALANCE.simulation.step;
    this.callbacks.render(frameDelta, alpha);
  };
}

function defaultRequestFrame(callback: (time: number) => void): number {
  return requestAnimationFrame(callback);
}

function defaultCancelFrame(handle: number): void {
  cancelAnimationFrame(handle);
}
